import React, { useState, useEffect, useMemo, useRef } from "react";
import { Stack, DefaultButton } from "@fluentui/react";
import { SendRegular, Notebook24Regular } from "@fluentui/react-icons";
import Send from "../../assets/Send.svg";
import Stop from "../../assets/Stop.svg";
import { usePromptTemplates } from "../../context/PromptTemplateContext";
import { AttachmentPills } from "../AttachmentPills/AttachmentPills";
import { ConversationAttachment } from "../../api";
import styles from "./QuestionInput.module.css";

interface Props {
  onSend: (question: string, isProposedAnswer: boolean) => void;
  onMicrophoneClick: (e: React.KeyboardEvent | React.MouseEvent) => void;
  onStopClick: (e: React.KeyboardEvent | React.MouseEvent) => void;
  disabled: boolean;
  isSendButtonDisabled: boolean;
  placeholder?: string;
  clearOnSend?: boolean;
  recognizedText: string;
  isListening: boolean;
  isRecognizing: boolean;
  isTextToSpeechActive: boolean;
  setRecognizedText: (text: string) => void;
  proposedAnswers?: string[];
  canRequestResolutionCode: boolean;
  onRequestResolutionCode: () => void;
  externalInputValue?: string; // Allow external control of input value
  onFileSelected?: (files: FileList) => void;
  attachments?: ConversationAttachment[];
  onDeleteAttachment?: (attachmentId: string) => void;
  isUploading?: boolean;
  uploadError?: { message: string; details?: string } | null;
  onDismissUploadError?: () => void;
  uploadWarning?: string | null;
  onDismissUploadWarning?: () => void;
  onStopGenerating?: () => void;
}

export const QuestionInput = ({
  onSend,
  
  disabled,
  isSendButtonDisabled,
  placeholder,
  clearOnSend,
  recognizedText,
  isRecognizing,
  setRecognizedText,
  isTextToSpeechActive,
  proposedAnswers,
  canRequestResolutionCode,
  onRequestResolutionCode,
  externalInputValue,
  onFileSelected,
  attachments,
  onDeleteAttachment,
  isUploading,
  uploadError,
  onDismissUploadError,
  uploadWarning,
  onDismissUploadWarning,
  onStopGenerating,
}: Props) => {

  const { templates } = usePromptTemplates();
  
  const [question, setQuestion] = useState<string>("");
  const [liveRecognizedText, setLiveRecognizedText] = useState<string>("");
  const [, setMicrophoneIconActive] = useState<boolean>(false);
  const [, setIsMicrophoneDisabled] = useState(false);
  const [isTextAreaDisabled, setIsTextAreaDisabled] = useState(false);
  const [templateHint, setTemplateHint] = useState<string | null>(null);
  const [showAutocompleteSuggestions, setShowAutocompleteSuggestions] = useState(false);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<{ promptKey: string; description?: string }[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const plusButtonRef = React.useRef<HTMLButtonElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [showPlusTooltip, setShowPlusTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

  // Focus the textarea when this input mounts (new chat session)
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Copilot-style picker state
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [expandedCategoryInPicker, setExpandedCategoryInPicker] = useState<string | null>(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);
  const templatePickerRef = useRef<HTMLDivElement>(null);

  const ACCEPTED_FILE_TYPES = ".png,.webp,.jpg,.jpeg,.svg,.pdf,.docx,.txt,.md,.csv,.json,.xml,.log";

  // Group templates by category for the Copilot-style inline picker.
  // Always include all known categories even if they have no templates yet.
  const ALL_CATEGORIES = ["Product Management", "R&D", "Professional Services", "Pre-sales", "O&M"];

  // Custom display order for Product Management templates (by seed PromptTemplateId).
  // Using IDs instead of promptKeys so renaming a key never breaks the order.
  const PM_TEMPLATE_ORDER_BY_ID: string[] = [
    "a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d", // vrp-initiative
    "b2c3d4e5-f6a7-4b5c-9d0e-1f2a3b4c5d6e", // vrp-bulk-prds
    "c3d4e5f6-a7b8-4c5d-0e1f-2a3b4c5d6e7f", // vrp-prd
    "d4e5f6a7-b8c9-4d5e-1f2a-3b4c5d6e7f8a", // vrp-bulk-epics
    "e5f6a7b8-c9d0-4e5f-2a3b-4c5d6e7f8a9b", // vrp-epic
    "f6a7b8c9-d0e1-4f5a-3b4c-5d6e7f8a9b0c", // vrp-stories
    "10000001-0001-4000-8000-000000000007", // vrp-diagram
    "a7b8c9d0-e1f2-4a5b-3c4d-6e7f8a9b0c1d", // vrp-architecture
    "10000001-0001-4000-8000-000000000004", // ears-ubiquitous
    "10000001-0001-4000-8000-000000000003", // ears-state-driven
    "10000001-0001-4000-8000-000000000001", // ears-event-driven
    "10000001-0001-4000-8000-000000000002", // ears-optional
    "10000001-0001-4000-8000-000000000005", // ears-unwanted-behavior
    "10000001-0001-4000-8000-000000000006", // cpaas-country
  ];

  const groupedTemplates = useMemo(() => {
    const groups: Record<string, typeof templates> = {};
    ALL_CATEGORIES.forEach(cat => { groups[cat] = []; });
    templates.forEach((t) => {
      const cat = t.category || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(t);
    });
    // Sort Product Management templates by their seed ID order.
    if (groups["Product Management"]) {
      groups["Product Management"].sort((a, b) => {
        const ai = PM_TEMPLATE_ORDER_BY_ID.indexOf(a.id);
        const bi = PM_TEMPLATE_ORDER_BY_ID.indexOf(b.id);
        // Templates not in the list go to the end, preserving their relative order.
        return (ai === -1 ? PM_TEMPLATE_ORDER_BY_ID.length : ai) - (bi === -1 ? PM_TEMPLATE_ORDER_BY_ID.length : bi);
      });
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [templates]);

  const templatesInCategory = useMemo(
    () => groupedTemplates.find(([cat]) => cat === expandedCategoryInPicker)?.[1] ?? [],
    [expandedCategoryInPicker, groupedTemplates]
  );
  void templatesInCategory; // available for future use

  const toggleCategoryInPicker = (category: string) => {
    setExpandedCategoryInPicker(prev => prev === category ? null : category);
  };

  const handleTemplatePick = (promptKey: string, name: string) => {
    const newVal = `/${promptKey} `;
    setQuestion(newVal);
    setLiveRecognizedText(newVal);
    setRecognizedText(newVal);
    updateTemplateHint(newVal);
    setSelectedTemplateName(name);
    setShowTemplatePicker(false);
    setExpandedCategoryInPicker(null);
  };

  // Close pickers when clicking outside
  useEffect(() => {
    if (!showTemplatePicker) return;
    const handler = (e: MouseEvent) => {
      if (templatePickerRef.current?.contains(e.target as Node)) return;
      setShowTemplatePicker(false);
      setExpandedCategoryInPicker(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showTemplatePicker]);

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onFileSelected && e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onFileSelected) return;
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFileSelected(files);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onFileSelected) {
      onFileSelected(files);
    }
    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Handle template hint display and autocomplete
   * When user types /<template_id>, show description as hint
   * When user adds non-whitespace content, clear the hint
   */
  const updateTemplateHint = (input: string) => {
    const slashMatch = input.trimStart().match(/^\/([a-z0-9-]*)\s*/);
    
    if (!slashMatch) {
      // Not a template command
      setTemplateHint(null);
      setShowAutocompleteSuggestions(false);
      setSelectedTemplateName(null);
      return;
    }

    const partialId = slashMatch[1];
    
    // Get autocomplete suggestions
    if (partialId.length > 0) {
      const suggestions = templates.filter((t) =>
        t.promptKey.toLowerCase().startsWith(partialId.toLowerCase())
      );
      setAutocompleteSuggestions(suggestions);
      setShowAutocompleteSuggestions(suggestions.length > 0);

      // If exact match found
      if (suggestions.length === 1 && suggestions[0].promptKey === partialId) {
        // Check if user has typed something after the template
        const afterCommand = input.substring(input.indexOf(partialId) + partialId.length).trim();
        const hasUserInput = afterCommand.length > 0;
        
        if (hasUserInput) {
          // User has typed something after the template, clear hint
          setTemplateHint(null);
        } else {
          // Show hint for the matched template
          setTemplateHint(suggestions[0].description);
        }
      }
    } else {
      // Show all templates when just "/" is typed
      setAutocompleteSuggestions(templates.slice(0, 5));
      setShowAutocompleteSuggestions(true);
      setTemplateHint(null);
    }
  };

  // Handle external input value changes (e.g., from template selection)
  useEffect(() => {
    if (externalInputValue !== undefined && externalInputValue !== question) {
      setQuestion(externalInputValue);
      setLiveRecognizedText(externalInputValue);
      setRecognizedText(externalInputValue);
      updateTemplateHint(externalInputValue);
    }
  }, [externalInputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isRecognizing) {
      setLiveRecognizedText(recognizedText);
      setIsTextAreaDisabled(true);
      setMicrophoneIconActive(true); // Set microphone icon to active (blue)
    } else {
      setIsTextAreaDisabled(false);
      setMicrophoneIconActive(false); // Set microphone icon to inactive
    }
  }, [recognizedText, isRecognizing]);

  useEffect(() => {
    setIsMicrophoneDisabled(isTextToSpeechActive);
  }, [isTextToSpeechActive]);

  const sendQuestion = () => {
    if (disabled || (!question.trim() && !liveRecognizedText.trim())) {
      return;
    }

    const textToSend = question || liveRecognizedText;

    onSend(textToSend, false);

    if (clearOnSend) {
      
      setQuestion("");
      setLiveRecognizedText("");
      setRecognizedText(""); // Clear recognizedText
      setTemplateHint(null);
      setShowAutocompleteSuggestions(false);
      setSelectedTemplateName(null);
    }
  };

  const onQuestionChange = (
    _ev: React.FormEvent<HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setQuestion(newValue || "");
    setLiveRecognizedText(newValue || ""); // Update liveRecognizedText when edited
    updateTemplateHint(newValue || "");
  };

  const handleProposedAnswerClick = (proposedAnswer: string) => {
    if (disabled) {
      return;
    }
    setQuestion("");
    setLiveRecognizedText("");
    setRecognizedText("");
    onSend(proposedAnswer, true);
  };

  const sendQuestionDisabled = disabled || !question.trim();
  void sendQuestionDisabled; // available for future use

  const handleRequestResolutionCode = () => {
    if (disabled) {
      return;
    }
    onRequestResolutionCode();
  }

  const handleAutocompleteSelect = (templateId: string) => {
    const updatedInput = `/${templateId} `;
    setQuestion(updatedInput);
    updateTemplateHint(updatedInput);
  }

  return (
    <Stack className={styles.questionInputRoot} data-tour="question-input">
      {/* Resolution code button */}
      {!disabled && canRequestResolutionCode && (
        <Stack className={styles.proposedAnswersContainer} tokens={{ childrenGap: 8 }}>
          <Stack horizontal={true} tokens={{ childrenGap: 8 }} wrap>
            <DefaultButton
                  key="request-resolution-code"
                  text="Give me the resolution code"
                  onClick={() => handleRequestResolutionCode()}
                  disabled={disabled}
                  className={styles.proposedAnswerButton}
                  data-testid={`request-resolution-code`}
                />
          </Stack>
        </Stack>
      )}

      {/* Template Hint */}
      {templateHint && (
        <div className={styles.templateHint} role="status" aria-live="polite">
          {templateHint}
        </div>
      )}

      {/* Autocomplete Suggestions */}
      {showAutocompleteSuggestions && autocompleteSuggestions.length > 0 && (
        <div className={styles.autocompleteSuggestions}>
          {autocompleteSuggestions.map((template, idx) => (
            <div
              key={idx}
              className={styles.autocompleteSuggestionItem}
              onClick={() => handleAutocompleteSelect(template.promptKey)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleAutocompleteSelect(template.promptKey);
                }
              }}
            >
              <div className={styles.suggestionName}>/{template.promptKey}</div>
              <div className={styles.suggestionDescription}>{template.description}</div>
            </div>
          ))}
        </div>
      )}

      {onFileSelected && (
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={ACCEPTED_FILE_TYPES}
          style={{ display: "none" }}
          aria-hidden="true"
        />
      )}

      <div
        className={styles.questionInputContainer}
        data-tour="input-container"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Middle area: attachments + textarea */}
        <div className={styles.middleArea}>
          {(attachments && (attachments.length > 0 || isUploading)) || uploadError || uploadWarning ? (
            <div className={styles.attachmentsInBox}>
              <AttachmentPills
                attachments={attachments || []}
                onDelete={onDeleteAttachment || (() => {})}
                isUploading={!!isUploading}
                uploadError={uploadError || null}
                onDismissError={onDismissUploadError}
                uploadWarning={uploadWarning || null}
                onDismissWarning={onDismissUploadWarning}
              />
            </div>
          ) : null}
          <textarea
            ref={textareaRef}
            disabled={isTextAreaDisabled}
            className={styles.questionInputField}
            placeholder={placeholder}
            value={question || liveRecognizedText}
            onChange={(e) => {
              const newValue = e.target.value;
              onQuestionChange(e, newValue);
              setRecognizedText(newValue);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendQuestion();
              }
            }}
            rows={1}
          />
        </div>

        {/* Copilot-style bottom toolbar */}
        <div className={styles.bottomToolbar}>
          {/* + Attach button */}
          <div className={styles.plusButtonWrapper}>
            <button
              type="button"
              ref={plusButtonRef}
              className={styles.plusButton}
              data-tour="attach-btn"
              aria-label="Attach file"
              onClick={handleAttachClick}
              onMouseEnter={() => {
                const rect = plusButtonRef.current?.getBoundingClientRect();
                if (rect) setTooltipPos({ top: rect.top, left: rect.left });
                setShowPlusTooltip(true);
              }}
              onMouseLeave={() => setShowPlusTooltip(false)}
              onFocus={() => {
                const rect = plusButtonRef.current?.getBoundingClientRect();
                if (rect) setTooltipPos({ top: rect.top, left: rect.left });
                setShowPlusTooltip(true);
              }}
              onBlur={() => setShowPlusTooltip(false)}
            >
              +
            </button>
            {showPlusTooltip && tooltipPos && (
              <div className={styles.plusTooltip} role="tooltip" style={{ top: tooltipPos.top, left: tooltipPos.left }}>
                <div className={styles.plusTooltipTitle}>Attach a file</div>
                <div className={styles.plusTooltipSection}>Supported types:</div>
                <div className={styles.plusTooltipTypes}>PDF, DOCX, TXT, MD, CSV, JSON, XML, LOG, PNG, JPG, JPEG, WEBP, SVG</div>
                <div className={styles.plusTooltipSection}>Max file size:</div>
                <div className={styles.plusTooltipTypes}>20 MB per file</div>
              </div>
            )}
          </div>

          {/* Unified cascading template picker */}
          {groupedTemplates.length > 0 && (
            <div className={styles.pickerWrapper} ref={templatePickerRef} data-tour="template-picker">
              <button
                type="button"
                className={`${styles.pickerButton} ${selectedTemplateName ? styles.pickerButtonActive : ''}`}
                onClick={() => setShowTemplatePicker(p => !p)}
                aria-label="Select template"
                aria-expanded={showTemplatePicker}
                aria-haspopup="listbox"
              >
                <Notebook24Regular style={{ width: 16, height: 16, color: '#ffffff' }} />
                <span className={styles.pickerLabel}>{selectedTemplateName ?? "Templates"}</span>
                <span className={styles.pickerChevron}>{showTemplatePicker ? "▲" : "▼"}</span>
              </button>
              {showTemplatePicker && (
                <div className={styles.pickerDropdown} role="listbox" aria-label="Prompt templates">
                  {groupedTemplates.map(([category, cats]) => (
                    <React.Fragment key={category}>
                      <div
                        role="option"
                        aria-selected={false}
                        aria-expanded={expandedCategoryInPicker === category}
                        className={`${styles.pickerDropdownItem} ${styles.pickerCategoryItem} ${expandedCategoryInPicker === category ? styles.pickerCategoryItemExpanded : ''}`}
                        onClick={() => toggleCategoryInPicker(category)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleCategoryInPicker(category); }}
                        tabIndex={0}
                      >
                        <span className={styles.pickerDropdownLabel}>{category.toUpperCase()}</span>
                        <span className={styles.pickerCount}>({cats.length})</span>
                        <span className={styles.pickerCategoryArrow}>{expandedCategoryInPicker === category ? '▼' : '▶'}</span>
                      </div>
                      {expandedCategoryInPicker === category && cats.map((template) => (
                        <div
                          key={template.id}
                          role="option"
                          aria-selected={selectedTemplateName === template.name}
                          className={`${styles.pickerDropdownItem} ${styles.pickerTemplateItem} ${selectedTemplateName === template.name ? styles.pickerDropdownItemActive : ''}`}
                          onClick={() => handleTemplatePick(template.promptKey, template.name)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTemplatePick(template.promptKey, template.name); }}
                          tabIndex={0}
                        >
                          <span className={styles.pickerTemplateIndent}>└</span>
                          <span className={styles.pickerDropdownLabel}>{template.name}</span>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Spacer pushes send button to right */}
          <div className={styles.toolbarSpacer} />

          {/* Send / Stop button */}
          <div className={styles.microphoneAndSendContainer}>
            {isSendButtonDisabled ? (
              <SendRegular className={styles.SendButtonDisabled} />
            ) : disabled && onStopGenerating ? (
              <div
                role="button"
                tabIndex={0}
                aria-label="Stop generating"
                onClick={onStopGenerating}
                onKeyDown={(e) => e.key === "Enter" || e.key === " " ? onStopGenerating() : null}
                className={styles.stopButtonContainer}
              >
                <span className={styles.stopButtonRing} aria-hidden="true" />
                <img src={Stop} className={styles.questionInputSendButton} alt="Stop generating" />
              </div>
            ) : (
              <div
                role="button"
                tabIndex={0}
                aria-label="Ask question button"
                onClick={sendQuestion}
                onKeyDown={(e) => e.key === "Enter" || e.key === " " ? sendQuestion() : null}
                className={styles.questionInputSendButtonContainer}
              >
                {disabled ? (
                  <SendRegular className={styles.questionInputSendButtonDisabled} />
                ) : (
                  <img src={Send} className={styles.questionInputSendButton} alt="Send" />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Stack>
  );
};
